import {ViewBreakpoints} from '@/constants/view-breakpoints';
import debounce from 'lodash/debounce';

/**
 * Get current breakpoint.
 */
const getCurrentBreakpoint = () => {
  let breakpoint = '';
  for (let b in ViewBreakpoints) {
    if (window.innerWidth > ViewBreakpoints[b] - 1 || !breakpoint) {
      breakpoint = b;
    } else {
      break;
    }
  }
  return breakpoint;
};

/**
 * Check whether the current breakpoint is less than given breakpoint.
 * @param breakpoint
 * @param currentBreakpoint
 * @return {boolean}
 */
const viewBreakpointLessThan = (breakpoint, currentBreakpoint = null) => {
  currentBreakpoint = currentBreakpoint ?? getCurrentBreakpoint();
  const breakpoints = Object.keys(ViewBreakpoints);
  return breakpoints.indexOf(breakpoint) > breakpoints.indexOf(currentBreakpoint);
};

/**
 * Check whether the current breakpoint is more than given breakpoint.
 * @param breakpoint
 * @param currentBreakpoint
 * @return {boolean}
 */
const viewBreakpointMoreThan = (breakpoint, currentBreakpoint = null) => {
  currentBreakpoint = currentBreakpoint ?? getCurrentBreakpoint();
  const breakpoints = Object.keys(ViewBreakpoints);
  return breakpoints.indexOf(breakpoint) < breakpoints.indexOf(currentBreakpoint);
};

/**
 * Check whether the given breakpoint is current or not. Accepts either a single value or an array.
 * viewBreakpointIs('small') or viewBreakpointIs(['small', 'medium']).
 * @param breakpoint
 * @param currentBreakpoint
 * @return {boolean|*}
 */
const viewBreakpointIs = (breakpoint, currentBreakpoint = null) => {
  currentBreakpoint = currentBreakpoint ?? getCurrentBreakpoint();
  return !Array.isArray(breakpoint) ? breakpoint === currentBreakpoint : breakpoint.includes(currentBreakpoint);
};

const ViewSizeMixin = {
  data() {
    return {
      viewBreakpoint: '',
    };
  },

  // Add listeners for when the window resizes
  created() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },

  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    /**
     * Check whether the given breakpoint is current or not. Accepts either a single value or an array.
     * viewBreakpointIs('small') or viewBreakpointIs(['small', 'medium']).
     * @param breakpoint
     * @return {*}
     */
    viewBreakpointIs(breakpoint) {
      return viewBreakpointIs(breakpoint, this.viewBreakpoint);
    },

    /**
     * Check whether the current breakpoint is less than given breakpoint.
     * @param breakpoint
     * @return {boolean}
     */
    viewBreakpointLessThan(breakpoint) {
      return viewBreakpointLessThan(breakpoint, this.viewBreakpoint);
    },

    /**
     * Check whether the current breakpoint is more than given breakpoint.
     * @param breakpoint
     * @return {boolean}
     */
    viewBreakpointMoreThan(breakpoint) {
      return viewBreakpointMoreThan(breakpoint, this.viewBreakpoint);
    },

    /**
     * Detect current view breakpoint.
     */
    detectViewBreakpoint() {
      let breakpoint = '';
      for (let b in ViewBreakpoints) {
        if (window.innerWidth > ViewBreakpoints[b] - 1 || !breakpoint) {
          breakpoint = b;
        } else {
          break;
        }
      }
      this.viewBreakpoint = breakpoint;
    },

    handleResize: debounce(
      function() {
        this.detectViewBreakpoint();
      },
      100,
      {leading: true}
    ),
  },
};

export {
  ViewSizeMixin as default,
  getCurrentBreakpoint,
  viewBreakpointMoreThan,
  viewBreakpointLessThan,
  viewBreakpointIs,
};
